import { Cell, FormControl, Grid, Heading, HFlow, InfoLabel, Tooltip } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import {
  createDecorator,
  DefaultFormFooter,
  Form,
  FormDebouncedValueSpy,
  FormPrompt,
  FormProps,
  FormRenderProps,
  RadioField,
  TextField,
} from 'components/form'
import { LotacaoEditableTable } from 'components/form/editable-table/LotacaoEditableTable'
import { ATIVIDADE_COLETIVA_GESTAO_GRUPOS_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { css } from 'emotion'
import { FormState } from 'final-form'
import { useSalvarAtividadeColetivaGruposMutation } from 'graphql/hooks.generated'
import { TipoGrupoParticipanteAtividadeColetivaEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import React, { Dispatch, Fragment, SetStateAction, useEffect } from 'react'
import { useHistory } from 'react-router'
import { metaPath } from 'util/metaPath'

import { createAtividadeColetivaGruposCalculations } from './calculator-atividadeColetivaGrupos'
import { CidadaoEditableTable } from './CidadaoEditableTable'
import { convertAtividadeColetivaGruposFormModelToQueryInput } from './converter-atividadeColetivaGrupos'
import { AtividadeColetivaGruposFormModel } from './model-atividadeColetivaGrupos'
import { convertCidadaoEditableTableToCpfCns } from './utils-atividadeColetivaGrupos'
import { atividadeColetivaGruposFormValidator } from './validator-atividadeColetivaGruposForm'

const path = metaPath<AtividadeColetivaGruposFormModel>()

const decorator = createDecorator(...createAtividadeColetivaGruposCalculations(path))

type AtividadeColetivaGruposFormProps = Pick<
  FormProps<AtividadeColetivaGruposFormModel>,
  'initialValues' | 'initialDirtyFields'
> & {
  setCachedValue: Dispatch<SetStateAction<AtividadeColetivaGruposFormModel>>
  clearCachedValue(): void
  unidadeSaudeNome: string
  fromView?: boolean
}

export function AtividadeColetivaGruposForm(props: AtividadeColetivaGruposFormProps) {
  const { setCachedValue, clearCachedValue, unidadeSaudeNome, fromView = false, ...rest } = props
  const history = useHistory()
  const { analytics } = useFirebase()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const [salvarAtividadeColetivaGrupo] = useSalvarAtividadeColetivaGruposMutation()
  const isEdicao = !!rest.initialValues.id

  const unidadeSaudeTooltipText = isEdicao
    ? 'A unidade vinculada não pode ser alterada'
    : 'Não é possível cadastrar um grupo em uma unidade diferente da sua lotação'

  useEffect(() => () => clearCachedValue(), [clearCachedValue])

  const goBack = () => {
    if (fromView) {
      history.push(`${ATIVIDADE_COLETIVA_GESTAO_GRUPOS_PATH}/visualizar/${rest.initialValues.id}`)
    } else {
      history.push(ATIVIDADE_COLETIVA_GESTAO_GRUPOS_PATH)
    }
  }

  const handleSubmit = (values: AtividadeColetivaGruposFormModel) => {
    return salvarAtividadeColetivaGrupo({
      variables: {
        input: convertAtividadeColetivaGruposFormModelToQueryInput(values),
      },
    })
      .then(() => {
        goBack()
        alert('success', `Grupo ${isEdicao ? 'editado' : 'cadastrado'} com sucesso.`)
        analytics.logEvent(`grupo_participante_ac_${isEdicao ? 'editado' : 'registrado'}`)
      })
      .catch(handleRejection)
  }

  const handleChange = (formState: FormState<AtividadeColetivaGruposFormModel>) => {
    setCachedValue({ ...formState.values })
  }

  const renderForm = (formProps: FormRenderProps<AtividadeColetivaGruposFormModel>) => {
    const { values, submitting, handleSubmit } = formProps
    const mustShowParticipantes = values.tipoGrupo === TipoGrupoParticipanteAtividadeColetivaEnum.ATIVIDADE_EM_SAUDE

    return (
      <Grid gap={2} style={styles.gridMarginTop}>
        <FormPrompt ignorePromptWhenSubmitting />
        <FormDebouncedValueSpy onChange={handleChange} />

        <Cell size={12}>
          <Tooltip text={unidadeSaudeTooltipText}>
            <span className={styles.tooltipSpan}>
              <InfoLabel title='Unidade de Saúde'>{unidadeSaudeNome}</InfoLabel>
            </span>
          </Tooltip>
        </Cell>

        <Cell size={4}>
          <TextField label='Nome do grupo' name={path.nome} maxLength={250} required />
        </Cell>

        <Cell size={4}>
          <FormControl label='Tipo de grupo' required>
            <HFlow style={styles.hflowMarginTop} hSpacing={1}>
              <RadioField
                name={path.tipoGrupo}
                value={TipoGrupoParticipanteAtividadeColetivaEnum.ATIVIDADE_EM_SAUDE}
                label='Atividade em saúde'
                style={styles.radioFieldMarginLeft}
              />
              <RadioField
                name={path.tipoGrupo}
                value={TipoGrupoParticipanteAtividadeColetivaEnum.REUNIAO}
                label='Reunião'
                style={styles.radioFieldMarginLeft}
              />
            </HFlow>
          </FormControl>
        </Cell>

        <Cell size={12} style={styles.cellPaddingTop}>
          <Heading level={2}>Profissionais envolvidos</Heading>
        </Cell>

        <Cell size={12}>
          <LotacaoEditableTable
            name={path.lotacoesEnvolvidas}
            excludeProfissionaisIds={values.lotacoesEnvolvidas?.map((item) => item?.lotacao?.profissional?.id) || []}
            cpfCnsCidadaosParticipantes={convertCidadaoEditableTableToCpfCns(values.cidadaosParticipantes)}
          />
        </Cell>

        {mustShowParticipantes && (
          <Fragment>
            <Cell size={12} style={styles.cellPaddingTop}>
              <Heading level={2}>Participantes</Heading>
            </Cell>

            <Cell size={12}>
              <CidadaoEditableTable
                name={path.cidadaosParticipantes}
                profissionaisEnvolvidos={values.lotacoesEnvolvidas?.map((item) => item?.lotacao?.profissional)}
              />
            </Cell>
          </Fragment>
        )}

        <Cell alignSelf='flex-end' size={12}>
          <DefaultFormFooter throttleSubmit loading={submitting} handleSubmit={handleSubmit} onCancel={goBack} />
        </Cell>
      </Grid>
    )
  }

  return (
    <Form<AtividadeColetivaGruposFormModel>
      {...rest}
      render={renderForm}
      decorators={[decorator]}
      onSubmit={handleSubmit}
      validate={atividadeColetivaGruposFormValidator()}
    />
  )
}

const styles = {
  gridMarginTop: css`
    margin-top: 2.5rem;
  `,
  cellPaddingTop: css`
    padding-top: 2.5rem;
  `,
  hflowMarginTop: css`
    margin-top: 0.5rem;
  `,
  radioFieldMarginLeft: css`
    margin-left: 0;
  `,
  tooltipSpan: css`
    display: inline-block;
  `,
}
